/**
 * useStarStore — FE-side "starred" overlay on top of the provider-truth
 * ``is_starred`` flag.
 *
 * Gmail / Outlook / Slack already track star/flag state per-user
 * server-side, so :prop:`InboxLiveItem.is_starred` is the canonical
 * read. This store is a **write-side optimistic cache**: when the user
 * stars/un-stars a row, we flip a local override immediately so the
 * UI feels instant, then fire the BE mutation (when the endpoint
 * lands) in the background. On a successful round-trip, the live feed
 * refetches and the override drops out of effect.
 *
 * BE status (June 2026)
 * ---------------------
 * The Gmail/Outlook star toggle endpoint isn't surfaced yet — only the
 * READ field is on the live feed. Until then this store is the
 * source-of-truth for STAR DECISIONS made inside Ando; provider-stars
 * coming back from the live feed merge with the local set so a star
 * made in Gmail.com still lights up in the inbox.
 *
 * Pattern reference: :file:`useSnoozeStore.ts` for the persist+evict
 * layout. No eviction here — stars are intentional, not time-bound.
 */

import { useCallback } from "react";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";


interface StarStoreState {
  /** ``message_id → true`` when starred locally. Absence ≠ unstarred —
   *  the provider's ``is_starred`` flag still applies. The store only
   *  remembers explicit LOCAL decisions. */
  readonly entries: Readonly<Record<string, boolean>>;
  readonly setStar: (messageId: string, starred: boolean) => void;
  readonly toggle: (messageId: string) => void;
  readonly clear: () => void;
}


export const useStarStore = create<StarStoreState>()(
  persist(
    (set, get) => ({
      entries: {},
      setStar: (messageId, starred) => {
        if (!messageId) return;
        const cur = get().entries;
        if (cur[messageId] === starred) return;
        const next = { ...cur };
        if (starred) next[messageId] = true;
        else delete next[messageId];
        set({ entries: next });
      },
      toggle: (messageId) => {
        if (!messageId) return;
        const cur = get().entries;
        const next = { ...cur };
        if (next[messageId]) delete next[messageId];
        else next[messageId] = true;
        set({ entries: next });
      },
      clear: () => set({ entries: {} }),
    }),
    {
      name: "ando-inbox-stars",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);


/**
 * Returns ``true`` when ``messageId`` is starred either server-side
 * (``providerStarred``) or locally. The selector subscribes only to
 * the one slot so re-renders are scoped.
 */
export function useIsStarred(
  messageId: string,
  providerStarred: boolean,
): boolean {
  const local = useStarStore((s) => s.entries[messageId] === true);
  return providerStarred || local;
}


/** Bulk helper for the action bar / hotkey path. */
export function useToggleStar(): (messageId: string) => void {
  const toggle = useStarStore((s) => s.toggle);
  return useCallback((id: string) => toggle(id), [toggle]);
}
